import {
  Alert,
  Badge,
  Group,
  Paper,
  Stack,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { IconAlertCircle, IconInfoCircle } from "@tabler/icons-react";

import {
  useGetAppUsage,
  useGetInsightsAnalysis,
  useGetSummary,
} from "../../api/insights";

const formatPercent = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${(n * 100).toFixed(1)}%`;
};

const engagementGrade = (score) => {
  if (score >= 70) return { label: "Strong", color: "green" };
  if (score >= 40) return { label: "Moderate", color: "yellow" };
  return { label: "Low", color: "orange" };
};

const ENGAGEMENT_INDEX_TOOLTIP =
  "Composite score (0–100) of how actively attendees used the app: agenda, sessions, features, sponsors, and polls/surveys. " +
  "Low (0–39): minimal use. Moderate (40–69): active use for the basics. Strong (70–100): consistent use across the app.";

const KpiCell = ({ label, value, color }) => (
  <Stack gap={4} px="lg" py="md">
    <Text size="xs" c="dimmed" fw={600} tt="uppercase" lh={1.3}>
      {label}
    </Text>
    <Title className="insights-kpi-value" order={2} c={color}>
      {value}
    </Title>
  </Stack>
);

const EngagementCard = ({ score, grade }) => (
  <Paper
    withBorder
    radius="md"
    p="lg"
    bg="var(--mantine-primary-color-light)"
    className="insights-engagement-card"
    style={{
      flex: "1 1 260px",
      borderColor: "var(--mantine-color-blue-2)",
    }}
  >
    <Stack gap={6}>
      <Group gap={8} align="center" wrap="wrap">
        <Text size="xs" c="dimmed" fw={600} tt="uppercase" lh={1.3}>
          Engagement index
        </Text>
        <Badge variant="light" color={grade.color} size="sm">
          {grade.label}
        </Badge>
        <Tooltip label={ENGAGEMENT_INDEX_TOOLTIP} multiline w={280} withArrow>
          <IconInfoCircle
            size={14}
            style={{ color: "var(--mantine-color-dimmed)", flexShrink: 0 }}
          />
        </Tooltip>
      </Group>
      <Title className="insights-kpi-value" order={1} c={grade.color}>
        {score}
      </Title>
    </Stack>
  </Paper>
);

function EventSummary({ eventId }) {
  const { data, isPending, isError, error } = useGetSummary(eventId);
  const { data: appUsage, isPending: appUsagePending } =
    useGetAppUsage(eventId);
  const { data: analysis } = useGetInsightsAnalysis(eventId);

  const isOn = (key) => analysis?.sections?.[key]?.isEnabled !== false;
  const grade = engagementGrade(data?.engagementIndex ?? 0);
  const showEngagement =
    isOn("engagementIndex") && (isPending || data?.engagementIndex != null);
  const appUsageGrade = engagementGrade(appUsage?.score ?? 0);
  const showCheckInRate =
    isOn("checkInRate") && (isPending || Number(data?.checkInRate) > 0);
  const showSponsorReach =
    isOn("sponsorReach") &&
    (isPending || Number(data?.sponsorInteractionRate) > 0);

  return (
    <Stack gap="sm">
      {isError && (
        <Alert
          color="red"
          icon={<IconAlertCircle size={16} />}
          title="Failed to load summary"
        >
          {error?.message || "Could not load the event summary."}
        </Alert>
      )}

      <Group
        align="stretch"
        gap="md"
        wrap="wrap"
        className="insights-summary-row"
      >
        {showEngagement && (
          <EngagementCard
            score={isPending ? "—" : `${data.engagementIndex}/100`}
            grade={grade}
          />
        )}
        <Paper
          withBorder
          radius="md"
          className="insights-kpi-panel-wrap"
          style={{ flex: "3 1 520px", overflow: "hidden" }}
        >
          <div className="insights-kpi-panel">
            {isOn("registeredAttendees") && (
              <KpiCell
                label="Registered attendees"
                value={
                  isPending
                    ? "—"
                    : (data?.totalRegisteredAttendees ?? 0).toLocaleString()
                }
              />
            )}
            {showCheckInRate && (
              <KpiCell
                label="Check-in rate"
                value={isPending ? "—" : formatPercent(data?.checkInRate)}
              />
            )}
            {isOn("appFeatureUsage") && (
              <KpiCell
                label="App feature usage"
                value={
                  appUsagePending || appUsage?.score == null
                    ? "—"
                    : `${appUsage.score}%`
                }
                color={appUsageGrade.color}
              />
            )}
            {showSponsorReach && (
              <KpiCell
                label="Sponsor reach"
                value={
                  isPending ? "—" : formatPercent(data?.sponsorInteractionRate)
                }
              />
            )}
          </div>
        </Paper>
      </Group>
    </Stack>
  );
}

export default EventSummary;
